import React, { useState, useEffect } from 'react';

import { Grid, MenuItem, Select, OutlinedInput, Button, IconButton } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import { useDispatch } from 'react-redux';
import ActionsContainer from 'screens/admin/Reviews/ReviewProcess/ReviewCycle/actionsContainer';
import styled from 'styled-components';
import { StyledTypography, VerticallyAlignedFlexContainer } from 'styles/common';
import { checkNewNavEnabled } from 'utils/helpers/featureFlags';

import { KH_LINKS } from 'constants/knowledgeHubLinks';
import theme from 'constants/theme';

import { getReminders, saveReminders } from 'services/engagement';
import { showErrorSnackbar } from 'services/snackbar';

import TooltipInfo from 'uiToolkit/TooltipInfo';

import { DeerFooterContainer } from './DeerFooterContainer';

export const AUDIENCE_TYPES = {
  REVIEWER: 'REVIEWER',
  MANAGER_SUMMARY: 'MANAGER_SUMMARY',
};

const SURVEY_REMINDER_AUDIENCE_MAPPINGS = {
  REVIEWER: 'Send reminder to participant',
  MANAGER_SUMMARY: 'Send summary email to manager of participant',
};

const EMPTY_REMINDER = {
  daysBeforeDeadline: null,
  audience: AUDIENCE_TYPES.REVIEWER,
};

const Reminders = ({ formId, backFn, nextFn }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [reminders, setReminders] = useState([EMPTY_REMINDER]);

  const getData = () => {
    setLoading(true);
    getReminders({ surveyId: formId })
      .then((res) => {
        res.length > 0 && setReminders(res);
      })
      .catch((err) => {
        dispatch(showErrorSnackbar('Sorry, unable to fetch the reminders!'));
      })
      .finally(() => setLoading(false));
  };

  useEffect(getData, []);

  const addDeadline = () => setReminders([...reminders, EMPTY_REMINDER]);

  const handleDeadlineChange = (value, index, key) => {
    const formattedValue = value.replaceAll(/[e.+-]/g, '');
    setReminders(
      reminders.map((reminder, idx) => {
        if (idx === index) {
          return { ...reminder, [key]: formattedValue };
        }
        return reminder;
      })
    );
  };
  const removeDeadline = (index) => {
    setReminders(reminders.filter((reminder, idx) => idx !== index));
  };

  const save = () => {
    setLoading(true);

    // filter the reminder entries without no. of days
    const nonEmptyReminders = reminders.filter((reminder) => reminder.daysBeforeDeadline !== null);
    const body = nonEmptyReminders.map(({ daysBeforeDeadline, audience }) => ({
      daysBeforeDeadline,
      audience,
    }));

    saveReminders({ surveyId: formId, body })
      .then((res) => {
        nextFn();
      })
      .catch((err) => {
        dispatch(showErrorSnackbar('Sorry, unable to save the reminders'));
      })
      .finally(() => setLoading(false));
  };

  return (
    <>
      <VerticallyAlignedFlexContainer>
        <StyledTypography variant="h5" customColor={theme.palette.text.heading} bottomGutter={1}>
          Reminders
        </StyledTypography>
        <TooltipInfo
          leftGutter={1}
          title={KH_LINKS.ENGAGEMENT_REMINDERS.description}
          linkUrl={KH_LINKS.ENGAGEMENT_REMINDERS.url}
        />
      </VerticallyAlignedFlexContainer>
      <StyledTypography variant="body" customColor={theme.palette.text.label}>
        Employees will receive reminder notifications on their preferred channels.{' '}
      </StyledTypography>

      <FormContainer>
        <>
          <Grid
            container
            style={{
              padding: '1rem 0 2rem',
              marginBottom: '1.5rem',
              borderBottom: `1px solid ${theme.palette.borders.lighterGrey}`,
            }}
          >
            <Grid item xs={8}>
              <StyledTypography color="#333">Reminder list</StyledTypography>
            </Grid>
            <Grid item xs={4} style={{ textAlign: 'right' }}>
              <StyledTypography color="#999" display="inline">
                Number of reminders
              </StyledTypography>
              <StyledTypography1 color="#999" leftGutter={2} display="inline">
                {reminders.length}
              </StyledTypography1>
            </Grid>
          </Grid>
          <>
            {reminders.length > 0 &&
              reminders.map(({ daysBeforeDeadline, audience }, index) => (
                <StyledGrid container>
                  <Grid xs={7}>
                    <StyledTypography color="#666" display="inline" rightGutter={2}>
                      Send reminder notifications
                    </StyledTypography>
                    <StyledOutlinedInput
                      value={daysBeforeDeadline}
                      placeholder="Number"
                      type="number"
                      min="0"
                      onChange={(e) => handleDeadlineChange(e.target.value, index, 'daysBeforeDeadline')}
                      labelWidth={0}
                    />
                    <StyledTypography color="#666" display="inline" leftGutter={2}>
                      days before the due date
                    </StyledTypography>
                  </Grid>
                  <Grid xs={4} style={{ textAlign: 'right' }}>
                    <StyledSelect
                      value={audience}
                      onChange={(e) => handleDeadlineChange(e.target.value, index, 'audience')}
                    >
                      {Object.keys(SURVEY_REMINDER_AUDIENCE_MAPPINGS).map((audience) => (
                        <MenuItem value={audience}>{SURVEY_REMINDER_AUDIENCE_MAPPINGS[audience]}</MenuItem>
                      ))}
                    </StyledSelect>
                  </Grid>
                  <Grid xs={1}>
                    <StyledIconButton onClick={() => removeDeadline(index)}>
                      <RemoveIcon />
                    </StyledIconButton>
                  </Grid>
                </StyledGrid>
              ))}
            <StyledButton
              startIcon={<AddIcon />}
              variant="contained"
              size="medium"
              color="primary"
              onClick={() => addDeadline()}
            >
              {reminders.length === 0 ? 'Add' : 'Add More'}
            </StyledButton>
          </>
        </>
      </FormContainer>

      <ActionsContainer
        ContainerComp={checkNewNavEnabled() ? DeerFooterContainer : null}
        loading={loading}
        onBackClick={backFn}
        onNextClick={nextFn && save}
        nextDisabled={false}
      />
    </>
  );
};

const FormContainer = styled.div`
  background: white;
  border: 1px solid #eaeaea;
  margin-top: 1.5rem;
  margin-bottom: 5rem;
  padding: 1rem 1.5rem 2.5rem 1.5rem;
`;

const StyledOutlinedInput = styled(OutlinedInput)`
  text-align: center;
  font-size: 0.9rem;
  input {
    width: 7rem;
    padding: 9px 8px;
    text-align: center;
    margin-right: 5px;
  }
  fieldset {
    border-color: #fff;
  }
  .MuiOutlinedInput-notchedOutline {
    border-color: #e9e9e9 !important;
    border-radius: 2px !important;
    border-width: 1px !important;
  }
  p {
    font-size: 0.9rem;
  }
`;

const StyledButton = styled(Button)`
  background: white;
  border: 1px solid #e9e9e9;
  box-shadow: none;
  color: #999;
  text-transform: capitalize;
  padding: 5px 1.25rem;
  &:hover {
    background: white;
    box-shadow: none;
  }
`;

const StyledSelect = styled(Select)`
  .MuiSelect-select {
    color: #333;
    border: 1px solid #f1f1f1;
    padding: 0.6rem 2rem 0.6rem 0.85rem;
    &:focus {
      background: #fff;
    }
  }
  &:after,
  &:before {
    display: none;
  }
`;

const StyledTypography1 = styled(StyledTypography)`
  background: #f1f1f1;
  padding: 6px 3rem;
  border: 1px solid #e9e9e9;
`;

const StyledGrid = styled(Grid)`
  margin-bottom: 1rem;
`;

const StyledIconButton = styled(IconButton)`
  margin-left: 2rem;
`;

export default Reminders;
